<template>
  <el-dialog
    class="location-selector-dialog"
    width="60%"
    :visible.sync="showDialog"
    :title="$t(`${i18nPrefix}.btn.selectLocation`)"
    :close-on-click-modal="false"
    @open="handleDialogOpen"
  >
    <TableList
      ref="tableList"
      class="location-list"
      no-operation
      :loading="loading"
      :list-data="searchResult"
      :table-columns="tableColumns"
      :createBtnLabel="$t(`${i18nPrefix}.btn.createLocation`)"
      @create="handleCreateLocationBtnClick"
    >
      <template #filter-group>
        <el-select
          v-model="search.cityId"
          clearable
          filterable
          class="city-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.filterCity`)"
          @change="searchLocationList"
        >
          <el-option v-for="(city) in locationCityList" :key="city.id" :label="city.i18nName" :value="city.id"></el-option>
        </el-select>
        <el-input
          v-model="search.keywords"
          clearable
          class="keywords-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.searchKeywords`)"
          @keypress.enter.native="searchLocationList"
        >
          <template #append>
            <div class="search-btn" @click="searchLocationList">
              <i class="el-icon-search"></i>
            </div>
          </template>
        </el-input>
      </template>
      <template #allCheckbox>
        <el-checkbox v-model="allCheckbox.selected" :indeterminate="allCheckbox.indeterminate" @change="handelCheckboxChange($event)"></el-checkbox>
      </template>
      <template #rowCheckbox="{ row }">
        <el-checkbox v-model="row.selected" @change="handelCheckboxChange($event, row)"></el-checkbox>
      </template>
    </TableList>
    <template #footer>
      <div class="footer-btn-group adapt-wrapper">
        <el-button class="footer-btn cancel-btn grey-btn" @click="handleCancelBtnClick">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="footer-btn save-btn blue-btn" @click="handleSaveBtnClick">{{ $t('common.btn.confirm') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import TableList from '@/components/tableList/common.vue'
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'

export default {
  components: {
    TableList
  },
  props: {
    companyId: {
      type: [Number, String],
      default: null
    },
    selected: {
      type: Array,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.job.editor',
      search: {
        cityId: null,
        keywords: null
      },
      locationList: [],
      locationIdMap: {},
      searchResult: [],
      loading: false,
      allCheckbox: {
        selected: false,
        indeterminate: false
      }
    }
  },
  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    },
    tableColumns () {
      const columns = [
        {
          width: 50,
          dataKey: 'id',
          headerSlotName: 'allCheckbox',
          slotName: 'rowCheckbox'
        },
        {
          width: 85,
          label: this.$t(`${this.i18nPrefix}.label.locationSelector.city`),
          dataKey: 'city.i18nName'
        },
        {
          width: 85,
          label: this.$t(`${this.i18nPrefix}.label.locationSelector.region`),
          dataKey: 'region.i18nName'
        },
        {
          width: 85,
          label: this.$t(`${this.i18nPrefix}.label.locationSelector.cbd`),
          dataKey: 'cbd.i18nName'

        },
        {
          width: 300,
          label: this.$t(`${this.i18nPrefix}.label.locationSelector.address`),
          dataKey: 'address'
        }
      ]
      return columns
    },
    locationCityList () {
      const city = {}
      const cityList = _.map(this.locationList, 'city')
      _.forEach(cityList, cityInfo => {
        if (!city[cityInfo.id]) {
          city[cityInfo.id] = cityInfo
        }
      })
      return Object.values(city)
    }
  },
  created () {

  },
  methods: {
    ...mapActions('company', ['getCompanyJobLocationList']),
    addI18nName (obj) {
      return addI18nProperty(obj, 'i18nName', 'cn_name', 'name')
    },
    async fetchLocationList () {
      this.loading = true
      try {
        const resp = await this.getCompanyJobLocationList({ company_id: this.companyId })
        if (resp && resp.result) {
          const locationIdMap = {}
          this.searchResult = this.locationList = _.map(resp.result, location => {
            this.addI18nName(location.city)
            this.addI18nName(location.city?.parent)
            this.addI18nName(location.region)
            this.addI18nName(location.cbd)
            location.selected = _.findIndex(this.selected, { id: location.id }) > -1
            locationIdMap[location.id] = location
            return location
          })
          this.locationIdMap = locationIdMap
        }
      } finally {
        this.loading = false
      }
    },
    searchLocationList () {
      this.loading = true
      const locationList = this.locationList
      const cityId = this.search.cityId
      const keywords = this.search.keywords
      let searchResult = locationList
      if (cityId || keywords) {
        searchResult = _.filter(locationList, location => {
          let searched = true
          if (cityId) {
            searched = location.city?.id === cityId
          }
          if (searched && keywords) {
            searched = location.address && location.address.includes(keywords)
          }
          return searched
        })
      }

      const selectedCount = _.countBy(searchResult, 'selected').true
      const searchResultCount = searchResult.length
      this.allCheckbox.selected = selectedCount === searchResultCount
      this.allCheckbox.indeterminate = !this.allCheckbox.selected && selectedCount > 0
      this.searchResult = searchResult
      this.loading = false
    },
    handleCreateLocationBtnClick () {
      window.open('/v-admin/job-location/management/list')
    },
    handelCheckboxChange (selected, location) {
      const allCheckbox = this.allCheckbox
      if (location) {
        const selectedCount = _.countBy(this.searchResult, 'selected').true
        const searchResultCount = this.searchResult.length
        allCheckbox.selected = selectedCount === searchResultCount
        allCheckbox.indeterminate = !allCheckbox.selected && selectedCount > 0
      } else {
        allCheckbox.indeterminate = false
        _.forEach(this.searchResult, location => {
          location.selected = selected
        })
      }
    },
    handleDialogOpen () {
      this.$nextTick(() => {
        this.$children[0].$el.scrollTo({ top: 0, left: 0 })
      })
    },
    handleSaveBtnClick () {
      this.$emit('change', _.filter(this.locationList, 'selected'))
      this.showDialog = false
    },
    handleCancelBtnClick () {
      this.showDialog = false
    }
  },
  watch: {
    showDialog: {
      immediate: true,
      handler () {
        if (this.showDialog) {
          this.fetchLocationList()
        }
      }
    },
    selected: {
      immediate: true,
      handler (newVal, oldVal) {
        _.forEach(oldVal, selected => {
          const location = this.locationIdMap[selected.id]
          if (!location) {
            return false
          }
          location.selected = false
        })
        _.forEach(newVal, selected => {
          const location = this.locationIdMap[selected.id]
          if (!location) {
            return false
          }
          location.selected = true
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.location-selector-dialog {
  .location-list {
    .filter-group {
      .keywords-filter {
        .search-btn {
          padding: 0 10px 0 0;
          font-size: 20px;
          cursor: pointer;
        }

        ::v-deep {
          .el-input__inner {
            border-right: 0;
          }

          .el-input-group__append {
            padding: 0;
            border-radius: 0;
            border-color: #b2b2b2;
          }
        }
      }
    }

    ::v-deep {
      .table-container {
        height: 42vh;
        overflow: auto;
      }

      .el-table {
        td {
          &.el-table__cell {
            padding: 10px 0;
          }

          .cell {
            padding: 0 10px;
          }
        }
      }
    }
  }

  .footer-btn-group {
    display: flex;
    justify-content: space-between;

    .footer-btn {
      padding: 10px 25px;
    }
  }
}
</style>
